"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PLAN_DISPLAY, ALL_FEATURES } from "@/lib/feature-display";
import { EmbeddedSubscribeCard } from "@/components/embedded-subscribe-card";

interface PricingCardsProps {
  isLoggedIn: boolean;
  userEmail?: string;
}

export function PricingCards({ isLoggedIn }: PricingCardsProps) {
  const router = useRouter();
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("monthly");

  const price = billingInterval === "monthly" ? PLAN_DISPLAY.monthlyPrice : PLAN_DISPLAY.annualPrice;
  const intervalLabel = billingInterval === "monthly" ? "/month" : "/year";
  const monthlySavings = Math.floor(PLAN_DISPLAY.monthlyPrice * 12 - PLAN_DISPLAY.annualPrice);

  function handleLoggedOutClick() {
    trackEvent("pricing_cta_click", { plan: billingInterval });
    router.push(`/login?callbackUrl=/pricing`);
  }

  return (
    <section className="pb-16 px-4">
      <div className="max-w-lg mx-auto">
        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 rounded-full p-1 flex gap-1">
            <button
              onClick={() => setBillingInterval("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billingInterval === "monthly"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval("annual")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billingInterval === "annual"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Annual
              <Badge className="ml-2 bg-green-100 text-green-700 border-0 text-xs">
                Save ${monthlySavings}
              </Badge>
            </button>
          </div>
        </div>

        {/* Single plan card */}
        <Card className="border-purple-500 border-2 shadow-lg relative">
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white border-0">
            30-Day Free Trial
          </Badge>
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">{PLAN_DISPLAY.name}</CardTitle>
            <CardDescription>Complete family protection</CardDescription>
            <div className="mt-4">
              <span className="text-5xl font-bold text-gray-900">
                ${price}
              </span>
              <span className="text-gray-500 ml-1">{intervalLabel}</span>
            </div>
            {billingInterval === "annual" && (
              <p className="text-sm text-green-600 mt-1">
                That&apos;s just ${(PLAN_DISPLAY.annualPrice / 12).toFixed(2)}/month
              </p>
            )}
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {ALL_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            {isLoggedIn ? (
              <EmbeddedSubscribeCard
                planId={billingInterval}
                triggerLabel="Subscribe Now"
                triggerClassName="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6 h-auto"
                onTriggerClick={() => trackEvent("pricing_cta_click", { plan: billingInterval })}
                onSubscribed={() => {
                  window.location.href = "/dashboard";
                }}
              />
            ) : (
              <Button
                onClick={handleLoggedOutClick}
                className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6 h-auto"
              >
                Start Free Trial
              </Button>
            )}
            <p className="text-xs text-gray-500 text-center">
              30-day free trial. Cancel anytime.
            </p>
          </CardFooter>
        </Card>
      </div>
    </section>
  );
}
